import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Typography,
  Button
} from '@mui/material';
import { ExpandMore, AddCircleOutline, EmojiEvents, Place } from '@mui/icons-material';
import StopMotionImage from 'components/play/StopMotionImage';
import { muscleLabel, locationLabel } from 'components/play/constants';

/**
 * OTRIX Play — exercise card with a 2-frame stop-motion preview, suggested
 * sets/reps, expandable instructions, and an action to add it to the weekly routine.
 * In the catalog/selection context the clickable image opens the detail modal; the
 * optional "register result" action only appears where a callback is provided.
 */
function PlayExerciseCard({ exercise, onAdd, onRegisterResult = null, onOpenDetail = null }) {
  const { t } = useTranslation(['play']);
  const [expanded, setExpanded] = useState(false);
  const clickableImage = typeof onOpenDetail === 'function';

  return (
    <Card
      sx={{
        border: '1px solid',
        borderColor: 'hairline.main',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1), background-color 200ms',
        '&:hover': { transform: 'translateY(-2px)', bgcolor: 'surface.raised' }
      }}>
      <Box
        role={clickableImage ? 'button' : undefined}
        tabIndex={clickableImage ? 0 : undefined}
        aria-label={clickableImage ? t('card.viewDetail', { ns: 'play', defaultValue: 'Ver ejercicio' }) : undefined}
        onClick={clickableImage ? () => onOpenDetail(exercise) : undefined}
        onKeyDown={
          clickableImage
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenDetail(exercise);
                }
              }
            : undefined
        }
        sx={{
          cursor: clickableImage ? 'pointer' : 'default',
          '&:hover .otx-play-frame': clickableImage ? { filter: 'brightness(1.06)' } : {}
        }}>
        <Box className="otx-play-frame" sx={{ transition: 'filter 200ms' }}>
          <StopMotionImage frame1={exercise.image_url_1} frame2={exercise.image_url_2} alt={exercise.localized_name} height={170} />
        </Box>
      </Box>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            {exercise.localized_name}
          </Typography>
          <IconButton
            size="small"
            aria-label={t('card.instructionsAria', { ns: 'play' })}
            onClick={() => setExpanded((v) => !v)}
            sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }}>
            <ExpandMore />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, gap: 0.5 }}>
          {exercise.muscle_group && (
            <Chip size="small" label={muscleLabel(t, exercise.muscle_group)} sx={{ bgcolor: 'gold.soft', color: 'gold.main' }} />
          )}
          {exercise.location && (
            <Chip size="small" variant="outlined" icon={<Place sx={{ fontSize: 16 }} />} label={locationLabel(t, exercise.location)} />
          )}
        </Stack>

        {(exercise.suggested_sets || exercise.suggested_reps) && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {exercise.suggested_sets ? `${exercise.suggested_sets} ${t('card.series', { ns: 'play' })}` : ''}
            {exercise.suggested_sets && exercise.suggested_reps ? ' · ' : ''}
            {exercise.suggested_reps ? `${exercise.suggested_reps} ${t('card.reps', { ns: 'play' })}` : ''}
          </Typography>
        )}

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, whiteSpace: 'pre-line' }}>
            {exercise.localized_instructions || exercise.localized_description || t('card.noInstructions', { ns: 'play' })}
          </Typography>
        </Collapse>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddCircleOutline />}
            onClick={() => onAdd(exercise)}
            sx={{ flex: 1 }}>
            {t('card.add', { ns: 'play' })}
          </Button>
          {typeof onRegisterResult === 'function' && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EmojiEvents />}
              onClick={() => onRegisterResult(exercise)}
              sx={{ flex: 1 }}>
              {t('card.result', { ns: 'play' })}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

PlayExerciseCard.propTypes = {
  exercise: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    localized_name: PropTypes.string,
    localized_description: PropTypes.string,
    localized_instructions: PropTypes.string,
    muscle_group: PropTypes.string,
    location: PropTypes.string,
    suggested_sets: PropTypes.string,
    suggested_reps: PropTypes.string,
    image_url_1: PropTypes.string,
    image_url_2: PropTypes.string
  }).isRequired,
  onAdd: PropTypes.func.isRequired,
  onRegisterResult: PropTypes.func,
  onOpenDetail: PropTypes.func
};

export default PlayExerciseCard;
